import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import TableFilters from "../../TableFilters";
import Timer from "../../Timer";
import {
  addStateSalesLead,
  editStateSalesLead,
  rowEditSalesLead,
  salesLeadFetching,
  salesLeadFilterText,
  selectedRowsStateSalesLead,
} from "../atoms/SalesLeadAtoms";
import SalesLeadForm from "../forms/SalesLeadForm";
import useDisableSalesLead from "../hooks/useDisableSalesLead";
import useGetSalesLead from "../hooks/useGetSalesLead";
import { SalesLeadData } from "../types/SalesLeadInterface";

export default function SalesLeadTable() {
  const {
    data: directData,
    isLoading: fetching,
    isSuccess: dataSuccess,
    refetch: reFetch,
  } = useGetSalesLead();

  const BulkDelete = useDisableSalesLead();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(addStateSalesLead);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateSalesLead
  );
  const [actionEditState, setActionEditState] =
    useRecoilState(editStateSalesLead);
  const [filterTextState, setFilterTextState] =
    useRecoilState(salesLeadFilterText);
  const setEditRowData = useSetRecoilState(rowEditSalesLead);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(salesLeadFetching);

  //Local States
  const [tableData, setTableData] = useState<SalesLeadData[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<SalesLeadData[]>(
    []
  );

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_configureagreement_saleslead_add;
  const editPermission =
    PERMISSIONS.permission_utilities_configureagreement_saleslead_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_configureagreement_saleslead_delete;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };
  useEffect(() => {
    if (dataSuccess && directData) {
      Loader();
    }
  }, [directData, dataSuccess]);

  //For filtered data update
  const [dataId, setDataId] = useState("");
  useEffect(() => {
    if (dataId != "") {
      if (dataId?.includes(".")) {
        let ids = dataId.split(".");
        const arr = ids.map((id) => Number(id));
        const filteredData = tableData?.filter((x) => arr.includes(x.id));
        setTableFilteredData(filteredData);
      } else {
        const filteredData = tableData?.filter((x) => x.id === Number(dataId));
        setTableFilteredData(filteredData);
      }
    }
  }, [dataId, tableData]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent;

  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setTableFilteredData(result);
          setDataId(recordIdFromRecent.recordId);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [history, recordIdFromRecent]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.id);
    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
    await Timer();
    reFetch();
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Sales Leads" : "Sales Lead"
    }?`}</div>
  );

  const filteredItems = tableData
    ? tableData?.filter(
        (item) =>
          item.name &&
          item.name
            .toLowerCase()
            .includes(filterTextState?.trim().toLowerCase())
      )
    : [];
  const matColumns = [
    {
      title: "Sales Lead Name",
      field: "name",
      customSort: (a, b) =>
        a.name && b.name
          ? a.name.trim().localeCompare(b.name.trim())
          : a.name
          ? 0
          : -1,
    },
    {
      title: "Employee ID",
      field: "employeeId",
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },

    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center mr-5">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];
  return (
    <>
      <div className="bg-white p-4">
        {actionAddState || actionEditState ? (
          <div>
            <SalesLeadForm refetch={reFetch} />
          </div>
        ) : (
          <>
            <div className="flex flex-row justify-between items-center">
              <TableFilters
                moduleDetails={"SalesLead"}
                textInputName={"name"}
                columnDetails={"Sales Lead Name"}
                filterText={setFilterTextState}
                disabledButton={
                  selectedRows !== undefined && selectedRows.length > 0
                }
                disabledTextBox={
                  selectedRows !== undefined && selectedRows.length > 0
                }
              />

              <div className="">
                {userPermissions.includes(addPermission) ? (
                  <>
                    <AddButton
                      buttonText="New Sales Lead"
                      onClick={() => handleAdd()}
                      disabled={
                        selectedRows !== undefined && selectedRows.length > 0
                      }
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
              <div></div>
              <MaterialTableTS
                data={
                  isFetching
                    ? []
                    : tableFilteredData?.length > 0
                    ? tableFilteredData
                    : filterTextState === ""
                    ? tableData
                    : filteredItems
                }
                showTitle={true}
                columns={matColumns}
                onSelectionChange={handleSelectedRowsChange}
                selectedRows={selectedRows}
                selection={userPermissions.includes(deletePermission)}
                isLoading={isFetching}
                setConfirmModalOpen={setOpenModalDelete}
              />
            </div>

            <ConfirmBoxDelete
              ModalTitle="Deactivate Sales Lead"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
                setSelectedRows([]);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </>
        )}
      </div>
    </>
  );
}
